import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { CssModuleType } from '@utils/cssModules';
import { useTranslation } from 'react-i18next';

import { useCioAutocomplete } from '@constructor-io/constructorio-ui-autocomplete';

import currentCssModule from './autoSuggest.module.scss';
import { Market } from '../../types';
import {
    getPriceForMarket,
    getApiKey,
    getDiscountPercent,
    priceRound,
    getObjectByDefaultVariant,
    formatNumber
} from '../../helpers';
import nopicture from './nopicture.jpeg';
import { fetchSearchResults } from 'src/api/constructor';

interface AutoSuggestProps {
    marketData: string;
    seachPage: string;
    isLoggedIn: boolean;
    discountData: string;
}

const AutoSuggest: React.FC<AutoSuggestProps> = ({
    marketData,
    isLoggedIn,
    seachPage,
    discountData
}) => {
    const st: CssModuleType = currentCssModule;

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [inputVal, setInputVal] = useState<string>('');
    const [market, setMarket] = useState<Market | null>(null);
    const [discount, setDiscount] = useState<number | null>(null);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams({ search: '' });

    const advancedParameters = useMemo(() => {
        if (market && market.MarketId.Value) {
            return {
                filters: {
                    'Products][Visible_To': market.MarketId.Value
                }
            };
        }
        return {};
    }, [market]);

    const {
        sections,
        getFormProps,
        getInputProps,
        getMenuProps,
        getItemProps
    } = useCioAutocomplete({
        apiKey: getApiKey(),
        onSubmit: (e) => submitSearch(e),
        placeholder: 'Search for products and brands',
        onFocus: () => setIsFocused(true),
        openOnFocus: true,
        onChange: (e) => setInputVal(e),
        advancedParameters
    });

    const handleInput = () => {
        const input = document.getElementById(
            'cio-autocomplete-0-input'
        ) as HTMLInputElement;
        const query = searchParams.get('search');

        if (!input) return;

        input.addEventListener('focusout', () => {
            setIsFocused(false);
        });

        if (!query) return;

        setTimeout(() => {
            input.value = query;
        }, 0);
    };

    useEffect(() => {
        handleInput();

        marketData && setMarket(JSON.parse(marketData));
        discountData && setDiscount(parseInt(JSON.parse(discountData)));
    }, []);

    const submitSearch = async (e) => {
        const { query, item } = e;
        await fetchSearchResults(query, {})
            .then((data) => {
                if (data.redirect?.data?.url) {
                    window.location.href = data.redirect.data.url;
                    return;
                }

                if (query) {
                    window.location.href = `${window.location.origin}${seachPage}?search=${encodeURIComponent(query)}`;
                    return;
                }
                
                if (item?.value) {
                    window.location.href = `${window.location.origin}${seachPage}?search=${encodeURIComponent(item?.value)}`;
                    return;
                }
                
            })
            .catch(console.error);
    };

    const renderNoResults = (results: number) => (
        <div className={st['no-results']}>
            {!isLoggedIn
                ? t('/Header/Search/NoResults')
                : results === 0 && `${t('/Search/NoResults')} ${inputVal}`}
        </div>
    );

    const renderImage = (item) => (
        <img
            width='200px'
            src={
                isLoggedIn
                    ? `${item?.image_url}?width=200&height=200&bgColor=white`
                    : nopicture
            }
            data-testid='cio-img'
        />
    );

    const renderPrice = (item, market) => {
        if (!discount && item.variations?.length === 1) {
            const price = getPriceForMarket(
                item.data,
                market.MarketName
            );
            const defaultPrice = formatNumber(price);
            return defaultPrice !== '0.00' ? `$${defaultPrice}` : null;
        }

        const prices = item.variations.map(({ data }) =>
            getPriceForMarket(data, market.MarketName)
        );

        const allEqual = prices.every((price) => price === prices[0]);

        if (allEqual) {
            if (discount !== 0) {
                const discountPrice = getDiscountPercent(
                    getPriceForMarket(item.data, market.MarketName),
                    discount
                );
                const finalPrice = !item.data.No_Additional_Discounts
                    ? discountPrice
                    : getPriceForMarket(item.data, market.MarketName);

                return finalPrice !== '0.00'
                    ? `$${parseFloat(finalPrice)?.toFixed(2)}`
                    : null;
            }
            const allEqualPrice = priceRound(prices[0]);

            return allEqualPrice !== '0.00' ? `$${allEqualPrice}` : null;
        } else {
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);

            if (discount !== 0 && !item.data.No_Additional_Discounts) {
                const rangeWithDiscount = `$${getDiscountPercent(
                    minPrice,
                    discount
                )} - $${getDiscountPercent(maxPrice, discount)}`;

                if (minPrice > 0.00 && maxPrice > 0.00) {
                    return rangeWithDiscount;
                }
                if (minPrice === 0.00)
                    return `$${getDiscountPercent(maxPrice, discount)}`;
                if (maxPrice === 0.00)
                    return `$${getDiscountPercent(minPrice, discount)}`;
            }

            const rangeWithoutDiscount = `$${priceRound(
                minPrice
            )} - $${priceRound(maxPrice)}`;

            if (minPrice > 0.00 && maxPrice > 0.00) {
                return rangeWithoutDiscount;
            }
            if (minPrice === 0.00) return `$${priceRound(maxPrice)}`;
            if (maxPrice === 0.00) return `$${priceRound(minPrice)}`;
        }
    };

    return (
        <div className={st['cio-autocomplete']}>
            <form {...getFormProps()} className={st['cio-form']}>
                <button
                    aria-label='Clear'
                    className={clsx(
                        st['cio-clear-btn'],
                        isFocused && st.focused
                    )}
                ></button>
                <input
                    {...getInputProps()}
                    className={clsx(st['cio-input'], isFocused && st.focused)}
                />
                <button
                    aria-label='Submit'
                    className={clsx(
                        st['cio-submit-btn'],
                        isFocused && st.focused
                    )}
                ></button>
            </form>
            <div
                {...getMenuProps()}
                className={clsx(
                    st['cio-results'],
                    isFocused && inputVal.length > 0 && st.isOpen
                )}
            >
                <>
                    {sections.map((section) => (
                        <div
                            key={section.identifier}
                            className={clsx(
                                st['cio-section-wrapper'],
                                section.identifier === 'Search Suggestions' &&
                                    section.data.length > 0 &&
                                    st.separator,
                                section.identifier
                            )}
                        >
                            <div className={st['cio-section']}>
                                <div className={st['cio-items']}>
                                    {section?.data?.slice(0, 5).map((item) => {
                                        const product =
                                            getObjectByDefaultVariant(item);
                                        return (
                                            <div
                                                {...getItemProps(product)}
                                                className={st['cio-item']}
                                                key={product?.id}
                                                {...(section.identifier !==
                                                'Search Suggestions'
                                                    ? {
                                                          onClick: (e) =>
                                                              e.preventDefault()
                                                      }
                                                    : {})}
                                            >
                                                {product?.image_url &&
                                                    renderImage(product)}
                                                <div
                                                    className={
                                                        st[
                                                            'cio-item-description'
                                                        ]
                                                    }
                                                >
                                                    <a
                                                        className={clsx(
                                                            st['cio-item-name'],
                                                            section.identifier ===
                                                                'Search Suggestions' &&
                                                                st[
                                                                    'cio-item-name--term'
                                                                ]
                                                        )}
                                                        href={product?.url}
                                                        onClick={() =>
                                                            window.location.href = product?.url
                                                                ? 
                                                                      product?.url
                                                                  
                                                                :
                                                                      `${window.location.origin}${seachPage}?search=${encodeURIComponent(
                                                                          item.id
                                                                      )}`
                                                                  
                                                        }
                                                    >
                                                        {item.value}
                                                    </a>
                                                    {section.identifier !==
                                                        'Search Suggestions' &&
                                                        isLoggedIn && (
                                                            <span
                                                                className={
                                                                    st[
                                                                        'cio-item-price'
                                                                    ]
                                                                }
                                                            >
                                                                {renderPrice(
                                                                    item,
                                                                    market
                                                                )}
                                                            </span>
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {section.identifier === 'Products' &&
                                        renderNoResults(section.data.length)}
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            </div>
        </div>
    );
};

export default AutoSuggest;
